import {Component, OnDestroy, OnInit, signal} from '@angular/core';
import {Router} from '@angular/router';
import {Location} from '@angular/common';
import {HttpContext, HttpErrorResponse} from '@angular/common/http';
import {environment} from '../environments/environment';
import {Subject, TimeoutError, finalize, retry, throwError, timeout, timer} from 'rxjs';
import {TaLayout} from './layout/ta-layout/ta-layout';
import {MsalService} from '@azure/msal-angular';
import {UserService} from '../api';
import {SessionService} from '../services/session.service';
import {ProgressBar} from 'primeng/progressbar';
import { TaAppStore } from '../state/clcta-store';
import {LoadingIndicatorComponent} from './components/loading-indicator/loading-indicator.component';
import {SUPPRESS_ERROR_TOAST} from '../util/error.interceptor';

const isTransientError = (e: unknown): boolean =>
  e instanceof TimeoutError ||
  (e instanceof HttpErrorResponse && (e.status === 0 || e.status >= 500));

@Component({
  selector: 'app-root',
  imports: [TaLayout, ProgressBar, LoadingIndicatorComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy{

  private unsubscribe: Subject<void> = new Subject<void>();
  public haveSession = signal(false);
  public isLoginError = signal(false);
  private SESSION_INTERVAL = 1 * 60 * 1000;
  private sessionPollInFlight = false;
  private hiddenSince = Date.now();
  private onVisibilityChange = () => {
    if (document.hidden) {
      this.hiddenSince = Date.now();
    } else if (Date.now() - this.hiddenSince >= this.SESSION_INTERVAL) {
      // Only re-check after an absence at least as long as a normal poll gap.
      this.getSession();
    }
  };

  constructor(
    private authService: MsalService,
    private userApi: UserService,
    private sessionService: SessionService, private router: Router,
    private location: Location,
    private store: TaAppStore) {
    console.log(' CLC TA Version: ', environment.appVersion);
    console.log(' CLC TA base: ', environment.tenantSettings.baseUrl);
    // console.log(' store? ', store);

    this.authService.handleRedirectObservable().subscribe({
      next: (result) => {

        if (!this.haveSession()) {
         this.getSession();

         setInterval(() => {
           if (!document.hidden) {
             this.getSession();
           }
         }, this.SESSION_INTERVAL);
         document.addEventListener('visibilitychange', this.onVisibilityChange);
        }

      },
      error: (error) => {
        console.log('handleRedirectObservable error: ', error);
        this.isLoginError.set(true);
        this.router.navigateByUrl('/', {skipLocationChange: true}).then();
      }
    });



  }

  public ngOnInit(): void {

  }

  public ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public getSession(): void {
    if (this.sessionPollInFlight) {
      return;
    }
    this.sessionPollInFlight = true;
    const lastHaveSession = this.haveSession();
    // console.log('  lastHaveSession: ', lastHaveSession);
    this.userApi.checkSession('body', false, {context: new HttpContext().set(SUPPRESS_ERROR_TOAST, true)}).pipe(
      timeout(30000),
      retry({count: 2, delay: (err) => isTransientError(err) ? timer(5000) : throwError(() => err)}),
      finalize(() => this.sessionPollInFlight = false)
    ).subscribe({
      next: (resp) => {
        // console.log('checkSession resp: ', resp);
        // console.log('  lastHaveSession2: ', lastHaveSession);
        this.sessionService.setSession(resp.data!);
        this.haveSession.set(true);

        if (!lastHaveSession) {
          this.store.updateSession(resp.data!);
          // Browser path, not router.url: a deep link's initial navigation is still held by its
          // guards (waiting on this session), so router.url is still '/' here.
          if (this.location.path() === '' || this.location.path() === '/') {
            this.router.navigateByUrl('/home');
          }
        }

      },
      error: (error) => {
        if (isTransientError(error)) {
          // Server or network blip: keep the session, the next interval retries.
          console.warn('checkSession transient error: ', error.status ?? error.name);
          return;
        }
        console.log('checkSession error: ', error);
        this.haveSession.set(false);
        this.router.navigateByUrl('/home').then();
      }
    })
  }


}
